import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import { Routes } from '~/domains/common/constants/routes';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButtonLink } from '~/domains/design-system/Button';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { isPathMatch } from '~/utils/isPathMatch';
import dt from '~/testing';

import type { AppShellHeaderProps } from '../../types';

import HeaderBgr from './assets/HeaderBgr';
import useStyles from './Header.styles';
import UserPill from './UserPill';
import Logo from './Logo';

type NavLinkProps = {
  href: string;
  text: string;
  currentPath: string;
  associatedPaths?: string[];
  isExact?: boolean;
  dataTestId?: string;
};
function NavLink({ href, text, currentPath, associatedPaths, isExact, dataTestId }: NavLinkProps) {
  const { classes, cx } = useStyles();
  const isActive = isPathMatch(currentPath, href, isExact, associatedPaths);

  return (
    <UnstyledButtonLink
      href={href}
      className={cx(classes.navLink, isActive && 'active')}
      data-test-id={dataTestId}
    >
      <Text variant="subhead-medium">{text}</Text>
    </UnstyledButtonLink>
  );
}

function Header({ user, handleDepositClick, handleUserClick }: AppShellHeaderProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('common');
  const router = useRouter();
  const currentPath = router.asPath;
  const isPredictionMarketsEnabled = useIsFeatureEnabled(FEATURE.ENABLE_PREDICTION_MARKETS);

  const isQuickPicks = isPathMatch(currentPath, Routes.quickPicksBoard(), false, ['/quick-picks']);
  const menuDataTestId = user
    ? dt.common.components.appShellHeader.homeButton
    : dt.components.shared.header.signedOutHeaderButton;

  return (
    <div className={classes.headerContainer} data-sticky-header>
      <div className={classes.headerContent}>
        <Logo isLoggedIn={!!user} />

        <div className={classes.navContainer}>
          <NavLink
            href={Routes.home()}
            isExact
            associatedPaths={['/']}
            text={t('appShellHeader3.nav.home')}
            currentPath={currentPath}
            dataTestId={menuDataTestId}
          />
          <NavLink
            href={Routes.contestLobby()}
            text={t('appShellHeader3.nav.lobby')}
            currentPath={currentPath}
            dataTestId={menuDataTestId}
          />
          <NavLink
            href={Routes.quickPicksBoard()}
            text={t('appShellHeader3.nav.quickPicks')}
            currentPath={currentPath}
            associatedPaths={['/quick-picks']}
            dataTestId={dt.components.shared.header.quickPicksButton}
          />
          {isPredictionMarketsEnabled && (
            <NavLink
              href={Routes.markets()}
              text={t('appShellHeader3.nav.predictionMarkets')}
              currentPath={currentPath}
              associatedPaths={['/markets']}
              dataTestId={dt.components.shared.header.predictionMarketsButton}
            />
          )}
          <NavLink
            href={Routes.marketplace()}
            text={t('appShellHeader3.nav.entryMarketplace')}
            currentPath={currentPath}
            associatedPaths={['/marketplace']}
            dataTestId={menuDataTestId}
          />
          {user && (
            <>
              <NavLink
                href={Routes.entries()}
                text={t('appShellHeader3.nav.myContests')}
                currentPath={currentPath}
                dataTestId={dt.common.components.appShellHeader.homeButton}
              />
              <NavLink
                href={Routes.commissioner()}
                associatedPaths={[
                  Routes.entrantsManager(''),
                  Routes.invitesManager(''),
                  Routes.manageContest(''),
                ]}
                text={t('appShellHeader3.nav.commissioner')}
                currentPath={currentPath}
                dataTestId={dt.common.components.appShellHeader.homeButton}
              />
            </>
          )}
        </div>

        <UserPill
          user={user}
          handleDepositClick={handleDepositClick}
          handleUserClick={handleUserClick}
        />
      </div>
      <HeaderBgr isQuickPicks={isQuickPicks} />
    </div>
  );
}

export default Header;
